import React, { FC, useCallback, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useToast } from 'Shared/components';
import { isMissing, isPresent } from 'Shared/utils';
import {
  ScreenHeader,
  VehiclesList,
  RoutePrompt,
  Footer,
  FooterNextButton,
  FooterPreviousButton,
  Body,
} from 'QuoteApp/components';
import { useFooter } from 'QuoteApp/utils/hooks';
import { transformSaveVehiclesRequest } from 'QuoteApp/services/endpoints/PostSaveVehicles';
import { EditProvider, FieldDataContext } from 'QuoteApp/utils/contexts';
import {
  formatVehicleDisplay,
  getVehicleDataByFieldValue,
  getVehicleDataCompleteness,
  vehicleValidation,
  getVehicleError,
  getErrorMessage,
} from 'QuoteApp/utils';
import { useAppContext } from 'QuoteApp/services/providers';
import { VehicleDriverAssignment } from 'QuoteApp/components/VehicleDriverAssignment';
import { VehicleMultiselectProvider } from 'QuoteApp/components/VehicleMultiselect/useVehicleMultiselect';
import { RewardsBanner } from 'QuoteApp/components/RewardsBanner';

export const Vehicles: FC = () => {
  const { previousStep, nextStep } = useFooter(1);
  const {
    details,
    setDetails,
    rate,
    refreshRate,
    api,
    rewardsEnrollmentResponse,
  } = useAppContext();
  const { toast } = useToast();

  const vehicleServerErrors = useMemo(
    () => getVehicleDataByFieldValue(details.vehicles, 'errors'),
    [details.vehicles]
  );

  const vehicleDataFieldsEditable = useMemo(
    () => getVehicleDataByFieldValue(details.vehicles, 'editable'),
    [details.vehicles]
  );

  const vehiclesData = useMemo(() => {
    return getVehicleDataByFieldValue(details.vehicles, 'value');
  }, [details]);

  const vehicleDataCompleteness = useMemo(() => {
    return getVehicleDataCompleteness(vehicleServerErrors);
  }, [vehicleServerErrors]);

  const VALIDATION_SCHEMA = Yup.object().shape({
    vehicles: Yup.object(
      Object.keys(details.vehicles).reduce(
        (acc, k) => ({
          ...acc,
          [k]: vehicleValidation,
        }),
        {}
      )
    ),
  });

  const formatEditingId = useCallback(
    (editingId: string | null) => {
      if (isMissing(editingId)) {
        return '';
      } else if (isPresent(vehiclesData[editingId])) {
        return formatVehicleDisplay(vehiclesData[editingId]);
      } else {
        // this should never happen?
        return '';
      }
    },
    [vehiclesData]
  );

  return (
    <>
      <Body data-testid="vehicles-page">
        {rewardsEnrollmentResponse?.isEnrolled && <RewardsBanner />}
        <ScreenHeader
          headerText="Vehicles"
          // TODO: Add when ADPF is implemented
          // subHeaderText="Select all vehicles the customer wishes to include on the policy."
        />
        <FieldDataContext.Provider
          value={{
            serverErrors: { vehicles: vehicleServerErrors },
            editableFields: { vehicles: vehicleDataFieldsEditable },
            alwaysShowErrors: true,
          }}
        >
          <EditProvider>
            {({ editingId, changeEditingId }) => (
              <VehicleMultiselectProvider>
                {({ isEditingMultiple }) => (
                  <Formik
                    enableReinitialize
                    validationSchema={VALIDATION_SCHEMA}
                    initialValues={{ vehicles: vehiclesData }}
                    onSubmit={async (
                      values,
                      { setSubmitting, setFieldError }
                    ) => {
                      try {
                        if (!isPresent(editingId)) {
                          throw new Error('Failed to edit');
                        }

                        /*  Handle post-submission vehicle form errors */
                        const postSubmissionVehicleError = getVehicleError(
                          values.vehicles[editingId],
                          isEditingMultiple
                        );
                        if (postSubmissionVehicleError) {
                          setFieldError(
                            `vehicles.${editingId}.submissionError`,
                            postSubmissionVehicleError
                          );
                          throw new Error(
                            'Please fix vehicle error before continuing.'
                          );
                        }

                        const response = await api.saveAppData(
                          {
                            data: {
                              values: values.vehicles,
                              editableFields: vehicleDataFieldsEditable,
                            },
                            transformRequest: transformSaveVehiclesRequest,
                          },
                          { suppressDefaultToastError: true }
                        );
                        rate && refreshRate();
                        setDetails(response.data);
                        changeEditingId(null);
                      } catch (e) {
                        toast({
                          type: 'error',
                          message: getErrorMessage(e),
                          withSecondaryNav: false,
                          icon: 'error',
                        });
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    {({ dirty, isValid, isSubmitting }) => (
                      <>
                        <RoutePrompt
                          when={dirty && !isSubmitting}
                          editingIdDisplay={formatEditingId(editingId)}
                          isValid={isValid}
                        />
                        <VehiclesList
                          dataCompleteness={vehicleDataCompleteness}
                          editingIdDisplay={formatEditingId(editingId)}
                          onVehiclesPage
                        />
                      </>
                    )}
                  </Formik>
                )}
              </VehicleMultiselectProvider>
            )}
          </EditProvider>
        </FieldDataContext.Provider>
        {details.vehicleDriverAssignment !== null && (
          <VehicleDriverAssignment
            showErrors={false}
            editable={details.drivers[details.principalInsuredId].editable}
          />
        )}
      </Body>
      <Footer
        nextButton={
          <FooterNextButton onClick={nextStep.onClickSuccess}>
            {nextStep.text}
          </FooterNextButton>
        }
        previousButton={
          <FooterPreviousButton onClick={previousStep.onClickSuccess}>
            {previousStep.text}
          </FooterPreviousButton>
        }
      />
    </>
  );
};
